import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HiOutlineArrowsUpDown, HiOutlinePlus, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineArrowRightOnRectangle, HiOutlineChevronUpDown } from 'react-icons/hi2';
import { useAuthStore } from '../../stores';
import { fetchMappingPage, insertMappingRow, updateMappingRow, deleteMappingRow } from '../../services/dataMappingsService';
import { SECTIONS, SECTION_BY_KEY, ColumnKind } from './meta';
import { SectionKey } from './types';
import MappingTable from './MappingTable';
import UploadCsvModal from './UploadCsvModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import './DataMappings.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const READONLY_KINDS: ColumnKind[] = ['readonly', 'readonlyStrong', 'readonlyNumber'];

/**
 * Atlas Admin — Data Mappings. A dedicated, full-screen admin area. Access is enforced server-side by Hasura permissions
 * (administrator role only); the redirect below is a client-side UX guard on top of that.
 */
function DataMappings() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'administrator';

  const [activeKey, setActiveKey] = useState<SectionKey>('customer');
  const section = SECTION_BY_KEY[activeKey];

  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState(section.defaultSortField);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [extraFilters, setExtraFilters] = useState<Record<string, string>>({});
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const queryColumns = useMemo(
    () => Array.from(new Set(['id', ...section.columns.map((c) => c.field)])),
    [section]
  );

  const dirtyCount = rows.filter((r) => r.__dirty).length;

  const confirmDiscard = () =>
    dirtyCount === 0 || window.confirm('You have unsaved changes. Continuing will discard them. Continue?');

  // Debounce free-text search 300ms so it doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Close the profile popup on outside click.
  useEffect(() => {
    if (!profileMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileMenuOpen]);

  const refetch = () => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    fetchMappingPage<Record<string, any>>(section.table, queryColumns, {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      sortField,
      sortDir,
      search: debouncedSearch || undefined,
      searchColumns: section.searchColumns,
      equalsFilter: section.extraFilters?.length
        ? Object.fromEntries(section.extraFilters.map((f) => [f.field, extraFilters[f.field] || undefined]))
        : undefined,
    })
      .then(({ rows: r, totalCount: c }) => {
        if (cancelled) return;
        setRows(r.map((row) => ({ ...row, __dirty: false, __isNew: false })));
        setTotalCount(c);
      })
      .catch(() => {
        if (!cancelled) toast.error(`Failed to load ${section.label}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  };

  useEffect(() => {
    const cleanup = refetch();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, section.key, page, pageSize, sortField, sortDir, debouncedSearch, extraFilters]);

  if (!isAdmin) return <Navigate to="/dashboard/opportunity" replace />;

  const handleSelectSection = (key: SectionKey) => {
    if (key === activeKey) return;
    if (!confirmDiscard()) return;
    setActiveKey(key);
    setPage(1);
    setSearchInput('');
    setDebouncedSearch('');
    setExtraFilters({});
    setSortField(SECTION_BY_KEY[key].defaultSortField);
    setSortDir('asc');
  };

  /** Three-state per column: asc -> desc -> back to the section's default sort. Clicking a
   * different column always starts it fresh at asc. */
  const handleSortFieldChange = (field: string) => {
    if (!confirmDiscard()) return;
    if (field === sortField) {
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortField(section.defaultSortField);
        setSortDir('asc');
      }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    if (!confirmDiscard()) return;
    setPageSize(size);
    setPage(1);
  };

  const handleExtraFilterChange = (field: string, value: string) => {
    if (!confirmDiscard()) return;
    setExtraFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handlePrevPage = () => {
    if (page <= 1) return;
    if (!confirmDiscard()) return;
    setPage((p) => p - 1);
  };

  const handleNextPage = () => {
    const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page >= pageCount) return;
    if (!confirmDiscard()) return;
    setPage((p) => p + 1);
  };

  const handleCellChange = (rowIndex: number, field: string, value: unknown) => {
    setRows((prev) => {
      const next = prev.slice();
      next[rowIndex] = { ...next[rowIndex], [field]: value, __dirty: true };
      return next;
    });
  };

  const handleAddRow = () => {
    if (!section.addable) return;
    const tempId = -Math.abs(Date.now()) - rows.length;
    setRows((prev) => [...prev, { id: tempId, ...section.emptyRow, __dirty: true, __isNew: true }]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const row = rows[rowIndex];
    if (row.__isNew) {
      setRows((prev) => prev.filter((_, i) => i !== rowIndex));
      return;
    }
    setDeleteRowIndex(rowIndex);
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setDeleteRowIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteRowIndex === null) return;
    const row = rows[deleteRowIndex];
    setDeleting(true);
    try {
      await deleteMappingRow(section.table, row.id);
      toast.success('Row deleted');
      setRows((prev) => prev.filter((_, i) => i !== deleteRowIndex));
      setTotalCount((c) => Math.max(0, c - 1));
      setDeleteRowIndex(null);
    } catch {
      toast.error('Failed to delete row');
    } finally {
      setDeleting(false);
    }
  };

  const handleDiscardAll = () => {
    refetch();
  };

  const handleSaveAll = async () => {
    const editableFields = section.columns.filter((c) => !READONLY_KINDS.includes(c.kind)).map((c) => c.field);
    const newRows = rows.filter((r) => r.__isNew);
    const editedRows = rows.filter((r) => r.__dirty && !r.__isNew);

    setSaving(true);
    try {
      await Promise.all([
        ...newRows.map((r) => {
          const object = Object.fromEntries(editableFields.map((f) => [f, r[f]]));
          return insertMappingRow(section.table, queryColumns, object);
        }),
        ...editedRows.map((r) => {
          const patch = Object.fromEntries(editableFields.map((f) => [f, r[f]]));
          return updateMappingRow(section.table, r.id, patch);
        }),
      ]);
      toast.success('Changes saved');
      refetch();
    } catch {
      toast.error('Failed to save changes — please try again');
    } finally {
      setSaving(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);
  const userInitials = (user?.name || user?.email || 'A').slice(0, 2).toUpperCase();

  return (
    <div className="dm-root">
      <aside className="dm-sidebar">
        <div className="dm-sidebar-header">
          <div className="dm-suite-title">Gatekeeper Intelligence Suite</div>
          <div className="dm-suite-sub">
            <HiOutlineArrowsUpDown />
            <span>DATA MAPPINGS</span>
          </div>
        </div>

        <nav className="dm-nav">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = s.key === activeKey;
            return (
              <div
                key={s.key}
                className={`dm-nav-item ${active ? 'active' : ''}`}
                onClick={() => handleSelectSection(s.key)}
              >
                <Icon className="dm-nav-icon" />
                <span>{s.label}</span>
              </div>
            );
          })}
        </nav>

        <div className="dm-sidebar-footer" ref={profileRef}>
          {profileMenuOpen && (
            <div className="dm-profile-menu">
              <div className="dm-profile-menu-item" onClick={() => navigate('/dashboard/opportunity')}>
                <HiOutlineArrowRightOnRectangle />
                <span>Back to Dashboard</span>
              </div>
            </div>
          )}
          <div className="dm-profile-row" onClick={() => setProfileMenuOpen((v) => !v)}>
            <span className="dm-profile-avatar">{userInitials}</span>
            <span className="dm-profile-name">{user?.name || 'Administrator'}</span>
            <HiOutlineChevronUpDown className="dm-profile-chevron" />
          </div>
        </div>
      </aside>

      <div className="dm-main">
        <div className="dm-header">
          <div className="dm-header-icon">
            <section.icon />
          </div>
          <div>
            <h1>{section.title}</h1>
            <p>{section.subtitle}</p>
          </div>
        </div>

        <div className="dm-toolbar">
          <div className="dm-search">
            <input
              className="dm-input"
              placeholder="Search this table"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="dm-toolbar-spacer" />
          {/*  {section.uploadMode !== 'disabled' && (
            <button type="button" className="dm-add-btn" onClick={() => setUploadModalOpen(true)}>
              <HiOutlineArrowUpTray />
              <span>Upload Data</span>
            </button>
          )} */}
          <span className="dm-help-line">
            Need help with these mappings? Reach out to{' '}
            <a href="mailto:aguatam@gatekeepersystems.com">aguatam@gatekeepersystems.com</a>
          </span>
          {section.addable && (
            <button type="button" className="dm-add-btn" onClick={handleAddRow}>
              <HiOutlinePlus />
              <span>Add Row</span>
            </button>
          )}
        </div>

        <div className="dm-toolbar dm-toolbar-secondary">
          <div className="dm-toolbar-field">
            <span>Show</span>
            <select className="dm-input dm-select-toolbar" value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {(section.extraFilters ?? []).map((f) => (
            <div className="dm-toolbar-field" key={f.field}>
              <span>{f.label}</span>
              <select
                className="dm-input dm-select-toolbar"
                value={extraFilters[f.field] || ''}
                onChange={(e) => handleExtraFilterChange(f.field, e.target.value)}
              >
                <option value="">{f.allLabel}</option>
                {f.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          ))}
          <div className="dm-toolbar-spacer" />
          <div className="dm-pagination">
            <span>{rangeStart}-{rangeEnd} of {totalCount}</span>
            <span>Page {page} of {pageCount}</span>
            <button type="button" className="dm-pager-btn" disabled={page <= 1} onClick={handlePrevPage}>
              <HiOutlineChevronLeft />
            </button>
            <button type="button" className="dm-pager-btn" disabled={page >= pageCount} onClick={handleNextPage}>
              <HiOutlineChevronRight />
            </button>
          </div>
        </div>

        <div className="dm-table-area">
          {loading ? (
            <div className="dm-loading">Loading…</div>
          ) : (
            <MappingTable
              columns={section.columns}
              rows={rows}
              deletable={section.deletable}
              sortField={sortField}
              sortDir={sortDir}
              onSortChange={handleSortFieldChange}
              onCellChange={handleCellChange}
              onDeleteRow={handleDeleteRow}
            />
          )}
        </div>

        {/* Unused: Uploads disabled currently */}
        <UploadCsvModal
          isOpen={uploadModalOpen}
          section={section}
          onClose={() => setUploadModalOpen(false)}
          onUploaded={() => {
            setUploadModalOpen(false);
            refetch();
          }}
        />

        <ConfirmDeleteModal
          isOpen={deleteRowIndex !== null}
          deleting={deleting}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />

        {dirtyCount > 0 && (
          <div className="dm-save-bar">
            <span>{dirtyCount} unsaved change(s)</span>
            <div className="dm-save-bar-actions">
              <button type="button" className="dm-discard-btn" onClick={handleDiscardAll} disabled={saving}>Discard</button>
              <button type="button" className="dm-save-btn" onClick={handleSaveAll} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataMappings;
